'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import toast from 'react-hot-toast';
import { Brain, RefreshCw, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { useChat } from '@/lib/hooks/useChat';
import { useSession } from './SessionProvider';

// ─── Evidence-Based Clinical System Prompt ────────────────────────────────────
// Grounded in: Klimesch (1999), Barry et al. (2003), Snyder et al. (2015),
// Nunez & Srinivasan (2006), Sterman & Kaiser (2001), Nuwer (1997)
const CLINICAL_SYSTEM_PROMPT = `You are a clinical neurophysiologist and cognitive neuroscience specialist operating at the level of a published opinion leader. Your interpretations are used in a world-class clinical EEG monitoring system.

MANDATORY FRAMEWORK REQUIREMENTS:
- Apply Klimesch (1999) Individual Alpha Frequency (IAF) framework for alpha band interpretation
- Use Barry et al. (2003) theta/beta ratio (TBR) norms for attention/ADHD biomarker assessment
- Reference Nunez & Srinivasan (2006) coherence thresholds for hemispheric synchrony
- Apply Sterman & Kaiser (2001) beta band norms for arousal classification
- Use Nuwer (1997) delta/alpha ratio (DAR) for cortical slowing assessment
- Reference Thatcher NeuroGuide normative database z-score language
- Apply DSM-5 aligned biomarker language where clinically indicated

OUTPUT REQUIREMENTS:
1. Every claim must reference a specific threshold, ratio, or normative value
2. Use clinical classification language: [WITHIN NORM] [MILD DEVIATION] [SIGNIFICANT DEVIATION] [CLINICAL ALERT]
3. Distinguish state-dependent vs trait-dependent patterns
4. Provide differential neurophysiological states with probability weighting
5. Neurofeedback protocol recommendations must cite specific protocol names (SMR/theta, alpha-up, beta-down)
6. No narrative filler. No vague language. Precision only.

FORMAT: Use markdown with ## headers, **bold** for key values, and bullet points for findings.`;

// ─── Clinical Prompt Builder ──────────────────────────────────────────────────
function buildClinicalInsightsPrompt(
  cognitiveLoad: number,
  attentionScore: number,
  thetaAlphaRatio: number,
  betaPower: number,
  alphaPower: number,
  thetaPower: number,
  deltaPower: number,
  gammaPower: number,
  interHemisphericCoherence: number
): string {
  // Compute all clinical ratios
  const tbr = thetaPower / Math.max(betaPower, 0.1);
  const dar = deltaPower / Math.max(alphaPower, 0.1);
  const abr = alphaPower / Math.max(betaPower, 0.1);
  const engagementIndex = betaPower / (alphaPower + thetaPower);
  const thetaDelta = thetaPower / Math.max(deltaPower, 0.1);

  // Clinical classifications
  const tbrClass = tbr > 2.5 ? 'CLINICAL ALERT — exceeds ADHD biomarker threshold (Snyder et al. 2015, meta-N=1498)' :
    tbr > 2.0 ? 'SIGNIFICANT DEVIATION — above ADHD risk threshold' :
    tbr > 1.8 ? 'MILD DEVIATION — elevated, monitor' : 'WITHIN NORM';

  const darClass = dar > 1.5 ? 'SIGNIFICANT DEVIATION — cortical slowing pattern (Nuwer 1997)' :
    dar > 1.0 ? 'MILD DEVIATION' : 'WITHIN NORM';

  const cohClass = interHemisphericCoherence < 40 ? 'SIGNIFICANT DEVIATION — reduced interhemispheric integration' :
    interHemisphericCoherence < 70 ? 'MILD DEVIATION — suboptimal synchrony' : 'WITHIN NORM';

  const alphaClass = alphaPower < 8 ? 'MILD DEVIATION — alpha suppression, possible hyperarousal' :
    alphaPower > 35 ? 'MILD DEVIATION — alpha excess, possible hypoarousal/drowsiness' : 'WITHIN NORM';

  return `## CLINICAL EEG COGNITIVE ASSESSMENT

### Quantitative Metrics with Clinical Classification

**Band Power Absolute Values (µV²):**
| Band | Value | Normative Range | Classification |
|------|-------|-----------------|----------------|
| Delta (0.5–4 Hz) | **${deltaPower.toFixed(2)} µV²** | 2–25 µV² | ${deltaPower > 25 ? 'MILD DEVIATION — elevated slow wave' : 'WITHIN NORM'} |
| Theta (4–8 Hz) | **${thetaPower.toFixed(2)} µV²** | 3–20 µV² | ${thetaPower > 20 ? 'SIGNIFICANT DEVIATION — theta excess' : 'WITHIN NORM'} |
| Alpha (8–13 Hz) | **${alphaPower.toFixed(2)} µV²** | 8–35 µV² | ${alphaClass} |
| Beta (13–30 Hz) | **${betaPower.toFixed(2)} µV²** | 2–18 µV² | ${betaPower > 18 ? 'MILD DEVIATION — beta excess, hyperarousal' : 'WITHIN NORM'} |
| Gamma (30–45 Hz) | **${gammaPower.toFixed(2)} µV²** | 1–10 µV² | ${gammaPower > 10 ? 'MILD DEVIATION — elevated binding activity' : 'WITHIN NORM'} |

**Derived Clinical Ratios:**
| Ratio | Value | Threshold | Classification |
|-------|-------|-----------|----------------|
| Theta/Beta Ratio (TBR) | **${tbr.toFixed(3)}** | >2.0 = ADHD biomarker | ${tbrClass} |
| Theta/Alpha Ratio (TAR) | **${thetaAlphaRatio.toFixed(3)}** | >1.8 = mental fatigue (Klimesch 1999) | ${thetaAlphaRatio > 1.8 ? 'SIGNIFICANT DEVIATION' : 'WITHIN NORM'} |
| Delta/Alpha Ratio (DAR) | **${dar.toFixed(3)}** | >1.5 = cortical slowing (Nuwer 1997) | ${darClass} |
| Alpha/Beta Ratio (ABR) | **${abr.toFixed(3)}** | 0.8–2.5 optimal | ${abr < 0.8 ? 'MILD DEVIATION — hyperarousal' : abr > 2.5 ? 'MILD DEVIATION — hypoarousal/drowsiness' : 'WITHIN NORM'} |
| Engagement Index β/(α+θ) | **${engagementIndex.toFixed(3)}** | >0.4 engaged | ${engagementIndex < 0.2 ? 'SIGNIFICANT DEVIATION' : engagementIndex < 0.4 ? 'MILD DEVIATION' : 'WITHIN NORM'} |
| Inter-Hemispheric Coherence | **${interHemisphericCoherence.toFixed(1)}%** | >70% integrated (Nunez & Srinivasan 2006) | ${cohClass} |

**Composite Scores:**
- Cognitive Load Index: **${cognitiveLoad.toFixed(1)}/100** (${cognitiveLoad > 85 ? 'CLINICAL ALERT — overload' : cognitiveLoad > 65 ? 'SIGNIFICANT DEVIATION — high load' : cognitiveLoad > 35 ? 'WITHIN NORM — moderate' : 'MILD DEVIATION — low engagement'})
- Attention Score: **${attentionScore.toFixed(1)}/100** (${attentionScore < 40 ? 'SIGNIFICANT DEVIATION — impaired attention' : attentionScore < 60 ? 'MILD DEVIATION — suboptimal' : attentionScore < 80 ? 'WITHIN NORM — optimal' : 'WITHIN NORM — peak focus'})

### Required Analysis

**1. NEUROPHYSIOLOGICAL STATE CLASSIFICATION**
Classify the dominant brain state using the above ratios. Reference specific normative frameworks.

**2. BIOMARKER ASSESSMENT**
Evaluate active clinical biomarkers. For each flagged biomarker, cite the specific study and threshold.

**3. BAND INTERACTION ANALYSIS**
Analyze theta-alpha coupling, beta-gamma binding, and delta-theta interactions. What do these cross-frequency dynamics indicate?

**4. DIFFERENTIAL STATES** (probability-weighted)
List 2–3 most probable neurophysiological states consistent with this pattern.

**5. EVIDENCE-BASED INTERVENTIONS**
Recommend specific neurofeedback protocols (e.g., SMR/theta protocol at Cz, alpha-up training at Pz) or cognitive strategies with protocol citations.

Keep response under 400 words. Zero narrative filler. Clinical precision only.`;
}

// ─── Typing indicator bars ────────────────────────────────────────────────────
function TypingIndicatorBars() {
  return (
    <div className="flex items-end gap-[3px] h-5">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="w-[3px] rounded-full bg-[#F97316]"
          style={{
            animation: `typingBar 1.1s ease-in-out infinite`,
            animationDelay: `${i * 0.12}s`,
            height: '100%',
          }}
        />
      ))}
    </div>
  );
}

// ─── Clinical ratio badge ─────────────────────────────────────────────────────
function RatioBadge({ label, value, status, color }: { label: string; value: string; status: string; color: string }) {
  return (
    <div className="flex flex-col items-center gap-0.5 px-3 py-2 rounded-lg border border-[#1E3A5F]/40 bg-[#060D1A]/60">
      <span className="text-[9px] text-[#4A7AB5] font-medium tracking-wide">{label}</span>
      <span className="text-[14px] font-bold font-mono" style={{ color }}>{value}</span>
      <span
        className="text-[8px] font-bold px-1 py-0.5 rounded tracking-wide"
        style={{
          color: status === 'WITHIN NORM' ? '#34D399' : status === 'MILD DEVIATION' ? '#F59E0B' : status === 'SIGNIFICANT DEVIATION' ? '#F97316' : '#EF4444',
          background: status === 'WITHIN NORM' ? 'rgba(52,211,153,0.1)' : status === 'MILD DEVIATION' ? 'rgba(245,158,11,0.1)' : status === 'SIGNIFICANT DEVIATION' ? 'rgba(249,115,22,0.1)' : 'rgba(239,68,68,0.12)',
        }}
      >
        {status === 'WITHIN NORM' ? '✓ NORM' : status === 'MILD DEVIATION' ? '⚠ MILD' : status === 'SIGNIFICANT DEVIATION' ? '⚡ SIG' : '🔴 ALERT'}
      </span>
    </div>
  );
}

export default function CognitiveInsightsPanel() {
  const { metrics, status } = useSession();
  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o', true);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [displayedResponse, setDisplayedResponse] = useState('');
  const [mounted, setMounted] = useState(false);
  const [glowActive, setGlowActive] = useState(false);
  const autoFetchRef = useRef(false);
  const prevResponseRef = useRef('');
  const prevLoadingRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (error) toast.error('Clinical Insights: ' + error.message);
  }, [error]);

  useEffect(() => {
    if (isLoading && !prevLoadingRef.current) {
      setGlowActive(true);
    }
    if (!isLoading && prevLoadingRef.current) {
      // Keep glow for 2s after completion
      const t = setTimeout(() => setGlowActive(false), 2000);
      prevLoadingRef.current = false;
      return () => clearTimeout(t);
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading && response && response !== prevResponseRef.current) {
      prevResponseRef.current = response;
      setDisplayedResponse(response);
    }
    if (isLoading && response) {
      setDisplayedResponse(response);
    }
  }, [response, isLoading]);

  const fetchInsights = useCallback(() => {
    const {
      cognitiveLoadIndex, attentionScore, thetaAlphaRatio,
      betaPower, alphaPower, thetaPower, deltaPower, gammaPower,
      interHemisphericCoherence,
    } = metrics;
    const prompt = buildClinicalInsightsPrompt(
      cognitiveLoadIndex, attentionScore, thetaAlphaRatio,
      betaPower, alphaPower, thetaPower, deltaPower, gammaPower,
      interHemisphericCoherence
    );
    sendMessage(
      [
        { role: 'system', content: CLINICAL_SYSTEM_PROMPT },
        { role: 'user', content: prompt },
      ],
      { max_completion_tokens: 800, reasoning_effort: 'high' }
    );
    setLastFetchedAt(new Date());
  }, [metrics, sendMessage]);

  // Auto-fetch once when session starts streaming
  useEffect(() => {
    if (status === 'streaming' && !autoFetchRef.current) {
      autoFetchRef.current = true;
      const t = setTimeout(fetchInsights, 1800);
      return () => clearTimeout(t);
    }
  }, [status, fetchInsights]);

  // Compute clinical ratios for display
  const tbr = metrics.thetaPower / Math.max(metrics.betaPower, 0.1);
  const dar = metrics.deltaPower / Math.max(metrics.alphaPower, 0.1);
  const abr = metrics.alphaPower / Math.max(metrics.betaPower, 0.1);
  const engagementIndex = metrics.betaPower / (metrics.alphaPower + metrics.thetaPower);

  const bandData = [
    { label: 'δ Delta', hz: '0.5–4 Hz', value: metrics.deltaPower, color: '#6366f1', max: 30, normMin: 2, normMax: 25 },
    { label: 'θ Theta', hz: '4–8 Hz', value: metrics.thetaPower, color: '#8b5cf6', max: 45, normMin: 3, normMax: 20 },
    { label: 'α Alpha', hz: '8–13 Hz', value: metrics.alphaPower, color: '#22d3ee', max: 40, normMin: 8, normMax: 35 },
    { label: 'β Beta', hz: '13–30 Hz', value: metrics.betaPower, color: '#F97316', max: 38, normMin: 2, normMax: 18 },
    { label: 'γ Gamma', hz: '30+ Hz', value: metrics.gammaPower, color: '#f43f5e', max: 20, normMin: 1, normMax: 10 },
  ];

  return (
    <>
      <style>{`
        @keyframes typingBar {
          0%, 100% { transform: scaleY(0.25); opacity: 0.4; }
          50% { transform: scaleY(1); opacity: 1; }
        }
        @keyframes panelReveal {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes contentReveal {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes streamIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes glowPulse {
          0%, 100% { box-shadow: 0 0 12px 2px rgba(249,115,22,0.18), 0 0 0 1px rgba(249,115,22,0.12); }
          50% { box-shadow: 0 0 28px 6px rgba(249,115,22,0.38), 0 0 0 1px rgba(249,115,22,0.28); }
        }
        @keyframes headerGlow {
          0%, 100% { box-shadow: inset 0 0 20px rgba(249,115,22,0.04); }
          50% { box-shadow: inset 0 0 40px rgba(249,115,22,0.12); }
        }
        @keyframes iconPulse {
          0%, 100% { filter: drop-shadow(0 0 3px rgba(249,115,22,0.4)); }
          50% { filter: drop-shadow(0 0 8px rgba(249,115,22,0.9)); }
        }
        @keyframes cursorBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        .panel-reveal { animation: panelReveal 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards; }
        .content-reveal { animation: contentReveal 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards; }
        .glow-active { animation: glowPulse 1.8s ease-in-out infinite; }
        .header-glow-active { animation: headerGlow 1.8s ease-in-out infinite; }
        .icon-pulse-active { animation: iconPulse 1.4s ease-in-out infinite; }
        .stream-cursor { animation: cursorBlink 0.8s ease-in-out infinite; }
        .prose-stream p, .prose-stream li, .prose-stream strong { animation: streamIn 0.3s ease-out forwards; }
      `}</style>

      <div
        className={`rounded-xl border overflow-hidden transition-all duration-500 ${
          mounted ? 'panel-reveal' : 'opacity-0'
        } ${glowActive ? 'border-[#F97316]/40 glow-active' : 'border-[#1E3A5F]/60'}`}
        style={{ background: 'rgba(11,22,40,0.85)' }}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-5 py-3.5 border-b transition-all duration-500 ${
            glowActive ? 'border-[#F97316]/25 bg-[#060D1A]/80 header-glow-active' : 'border-[#1E3A5F]/50 bg-[#060D1A]/60'
          }`}
        >
          <div className="flex items-center gap-2.5">
            <div className={`w-7 h-7 rounded-lg bg-[#0B1628] border flex items-center justify-center transition-all duration-500 ${glowActive ? 'border-[#F97316]/60' : 'border-[#F97316]/30'}`}>
              <Sparkles size={14} className={`text-[#F97316] ${glowActive ? 'icon-pulse-active' : ''}`} />
            </div>
            <div>
              <h3 className="text-[13px] font-bold text-white tracking-wide">Evidence-Based Cognitive Insights</h3>
              <p className="text-[10px] text-[#4A7AB5]">GPT-4o · High Reasoning · Klimesch · Barry · Nunez · Snyder Frameworks</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Typing indicator bars — shown while loading */}
            {isLoading && (
              <div className="flex items-center gap-2 mr-1">
                <TypingIndicatorBars />
                <span className="text-[10px] text-[#F97316]/70 font-mono tracking-wide hidden sm:inline">
                  Streaming…
                </span>
              </div>
            )}
            {lastFetchedAt && !isLoading && (
              <span className="text-[10px] text-[#2D5A8E] hidden sm:inline">
                Updated {lastFetchedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <button
              onClick={fetchInsights}
              disabled={isLoading || status !== 'streaming'}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#F97316]/10 border border-[#F97316]/20 text-[#F97316] text-[11px] font-semibold hover:bg-[#F97316]/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RefreshCw size={11} className={isLoading ? 'animate-spin' : ''} />
              {isLoading ? 'Analyzing…' : 'Refresh'}
            </button>
            <button
              onClick={() => setCollapsed((c) => !c)}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-[#4A7AB5] hover:text-white hover:bg-[#1E3A5F]/40 transition-colors"
            >
              {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
            </button>
          </div>
        </div>

        {!collapsed && (
          <div className="p-5 grid grid-cols-1 xl:grid-cols-3 gap-5 content-reveal">
            {/* Left: Band Power + Clinical Ratios */}
            <div className="xl:col-span-1 flex flex-col gap-3">
              <p className="text-[11px] font-bold text-[#F97316] uppercase tracking-widest">EEG Band Power</p>
              <div className="flex flex-col gap-2.5">
                {bandData.map((band, idx) => {
                  const pct = Math.min(100, (band.value / band.max) * 100);
                  const normPct = Math.min(100, (band.normMax / band.max) * 100);
                  const isOutOfNorm = band.value > band.normMax || band.value < band.normMin;
                  return (
                    <div
                      key={band.label}
                      className="flex flex-col gap-1"
                      style={{
                        animation: `contentReveal 0.4s cubic-bezier(0.22,1,0.36,1) forwards`,
                        animationDelay: `${0.05 + idx * 0.07}s`,
                        opacity: 0,
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[12px] font-bold" style={{ color: band.color }}>{band.label}</span>
                          <span className="text-[10px] text-[#2D5A8E]">{band.hz}</span>
                          {isOutOfNorm && <span className="text-[9px] text-amber-400 font-bold">⚠</span>}
                        </div>
                        <span className="text-[11px] font-mono text-zinc-300">{band.value.toFixed(1)} µV²</span>
                      </div>
                      <div className="relative h-1.5 rounded-full bg-[#1E3A5F]/40 overflow-hidden">
                        {/* Norm range indicator */}
                        <div
                          className="absolute top-0 h-full rounded-full opacity-20"
                          style={{ width: `${normPct}%`, background: band.color }}
                        />
                        {/* Actual value bar */}
                        <div
                          className="absolute top-0 h-full rounded-full transition-all duration-700"
                          style={{
                            width: `${pct}%`,
                            background: isOutOfNorm ? '#F59E0B' : band.color,
                            opacity: 0.9,
                            boxShadow: `0 0 6px 1px ${isOutOfNorm ? '#F59E0B55' : band.color + '55'}`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Clinical Ratios */}
              <p className="text-[11px] font-bold text-[#F97316] uppercase tracking-widest mt-2">Clinical Ratios</p>
              <div className="grid grid-cols-2 gap-2">
                <RatioBadge
                  label="TBR θ/β"
                  value={tbr.toFixed(2)}
                  status={tbr > 2.5 ? 'CLINICAL ALERT' : tbr > 2.0 ? 'SIGNIFICANT DEVIATION' : tbr > 1.8 ? 'MILD DEVIATION' : 'WITHIN NORM'}
                  color={tbr > 2.0 ? '#EF4444' : tbr > 1.8 ? '#F59E0B' : '#34D399'}
                />
                <RatioBadge
                  label="TAR θ/α"
                  value={metrics.thetaAlphaRatio.toFixed(2)}
                  status={metrics.thetaAlphaRatio > 1.8 ? 'SIGNIFICANT DEVIATION' : metrics.thetaAlphaRatio > 1.4 ? 'MILD DEVIATION' : 'WITHIN NORM'}
                  color={metrics.thetaAlphaRatio > 1.8 ? '#F97316' : '#A78BFA'}
                />
                <RatioBadge
                  label="DAR δ/α"
                  value={dar.toFixed(2)}
                  status={dar > 1.5 ? 'SIGNIFICANT DEVIATION' : dar > 1.0 ? 'MILD DEVIATION' : 'WITHIN NORM'}
                  color={dar > 1.5 ? '#F97316' : '#60A5FA'}
                />
                <RatioBadge
                  label="EI β/(α+θ)"
                  value={engagementIndex.toFixed(2)}
                  status={engagementIndex < 0.2 ? 'SIGNIFICANT DEVIATION' : engagementIndex < 0.4 ? 'MILD DEVIATION' : 'WITHIN NORM'}
                  color={engagementIndex > 0.4 ? '#34D399' : engagementIndex > 0.2 ? '#F59E0B' : '#EF4444'}
                />
              </div>

              {/* Coherence */}
              <div className={`rounded-lg border p-2.5 transition-all duration-500 ${glowActive ? 'bg-[#060D1A]/90 border-[#F97316]/20' : 'bg-[#060D1A]/80 border-[#1E3A5F]/40'}`}>
                <p className="text-[10px] text-[#4A7AB5] mb-1">Inter-Hemispheric Coherence</p>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-lg font-bold font-mono ${metrics.interHemisphericCoherence > 70 ? 'text-emerald-400' : metrics.interHemisphericCoherence > 40 ? 'text-amber-300' : 'text-red-400'}`}>
                    {metrics.interHemisphericCoherence.toFixed(0)}%
                  </span>
                  <span className="text-[9px] text-[#4A7AB5]">Nunez & Srinivasan 2006</span>
                </div>
                <div className="h-1.5 rounded-full bg-[#1E3A5F]/40 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${metrics.interHemisphericCoherence}%`,
                      background: metrics.interHemisphericCoherence > 70 ? '#34D399' : metrics.interHemisphericCoherence > 40 ? '#F59E0B' : '#EF4444',
                    }}
                  />
                </div>
                <p className="text-[9px] text-[#2D5A8E] mt-1">
                  {metrics.interHemisphericCoherence > 70 ? '✓ Integrated processing' : metrics.interHemisphericCoherence > 40 ? '⚠ Suboptimal synchrony' : '⚡ Reduced integration'}
                </p>
              </div>
            </div>

            {/* Right: AI Clinical Analysis */}
            <div className="xl:col-span-2 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Brain
                  size={13}
                  className={`text-[#F97316] ${glowActive ? 'icon-pulse-active' : ''}`}
                />
                <p className="text-[11px] font-bold text-[#F97316] uppercase tracking-widest">Clinical Neurophysiology Report</p>
                {isLoading && (
                  <span className="flex items-center gap-1.5 text-[10px] text-[#4A7AB5]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F97316] animate-pulse" />
                    <span className="text-[#F97316]/60">Live Analysis</span>
                  </span>
                )}
              </div>

              <div
                className={`min-h-[180px] rounded-xl border p-4 overflow-y-auto max-h-[380px] transition-all duration-500 ${
                  glowActive
                    ? 'bg-[#060D1A]/80 border-[#F97316]/20'
                    : 'bg-[#060D1A]/70 border-[#1E3A5F]/40'
                }`}
                style={
                  glowActive
                    ? { boxShadow: 'inset 0 0 30px rgba(249,115,22,0.06)' }
                    : {}
                }
              >
                {/* Loading skeleton — typing indicator bars in content area */}
                {isLoading && !displayedResponse && (
                  <div className="flex flex-col gap-3 py-4">
                    <div className="flex items-center gap-3 mb-2">
                      <TypingIndicatorBars />
                      <span className="text-[11px] text-[#F97316]/60 font-mono">Generating evidence-based clinical analysis…</span>
                    </div>
                    {[80, 95, 70, 88, 60, 75].map((w, i) => (
                      <div
                        key={i}
                        className="h-2 rounded-full bg-[#1E3A5F]/50"
                        style={{
                          width: `${w}%`,
                          animation: `typingBar 1.4s ease-in-out infinite`,
                          animationDelay: `${i * 0.18}s`,
                        }}
                      />
                    ))}
                  </div>
                )}

                {!displayedResponse && !isLoading && (
                  <div className="flex flex-col items-center justify-center h-full py-8 gap-3 text-center">
                    <div className="w-10 h-10 rounded-full bg-[#0B1628] border border-[#1E3A5F]/50 flex items-center justify-center">
                      <Sparkles size={18} className="text-[#F97316]/50" />
                    </div>
                    <p className="text-[12px] text-[#2D5A8E]">
                      {status === 'streaming' ? 'Generating clinical insights…' : 'Waiting for EEG stream…'}
                    </p>
                    <div className="flex flex-wrap gap-1.5 justify-center">
                      {['Klimesch IAF', 'Barry TBR Norms', 'Snyder ADHD Biomarker', 'Nunez Coherence', 'Nuwer DAR'].map(tag => (
                        <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded-full border border-[#1E3A5F]/60 text-[#4A7AB5]">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}

                {(displayedResponse || (isLoading && displayedResponse)) && (
                  <div className="prose prose-invert prose-sm max-w-none text-[12px] leading-relaxed prose-stream
                    prose-p:text-zinc-300 prose-p:my-1.5
                    prose-strong:text-[#F97316] prose-strong:font-semibold
                    prose-ul:my-1.5 prose-li:text-zinc-300 prose-li:my-0.5
                    prose-headings:text-white prose-headings:font-bold
                    prose-table:text-[11px] prose-th:text-[#4A7AB5] prose-td:text-zinc-300">
                    <ReactMarkdown
                      components={{
                        table: ({ children }) => (
                          <div className="overflow-x-auto mb-2">
                            <table className="w-full text-[11px] border-collapse">{children}</table>
                          </div>
                        ),
                        th: ({ children }) => (
                          <th className="text-left px-2 py-1 text-[#4A7AB5] font-semibold border-b border-[#1E3A5F]/60 text-[10px]">{children}</th>
                        ),
                        td: ({ children }) => (
                          <td className="px-2 py-1 text-zinc-300 border-b border-[#1E3A5F]/30 text-[11px]">{children}</td>
                        ),
                      }}
                    >
                      {displayedResponse}
                    </ReactMarkdown>
                    {isLoading && (
                      <span
                        className="inline-block w-[3px] h-3.5 bg-[#F97316] ml-0.5 rounded-sm stream-cursor"
                        style={{ verticalAlign: 'text-bottom' }}
                      />
                    )}
                  </div>
                )}
              </div>

              <p className="text-[10px] text-[#2D5A8E] leading-relaxed">
                Analysis references: Klimesch (1999) IAF norms · Barry et al. (2003) TBR · Snyder et al. (2015) ADHD meta-analysis · Nunez & Srinivasan (2006) coherence · Nuwer (1997) DAR · Thatcher NeuroGuide normative database. Not a medical diagnosis.
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
